import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import {
  List,
  ListItem,
  Box,
  Drawer,
  Typography,
  Divider,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { overrideRessource } from 'generic/utils/utils';
import ResultSingleActions from 'generic/components/ui/ResultSingleAction';
import CommentsContainer from 'generic/containers/CommentsContainer';
import MoreLikeThis from 'generic/components/ui/MoreLikeThis';

const Document = overrideRessource('components/documents/Document');

const ResultsComplete = ({
  canAddToCart,
  canAddToNewsletter,
  canComment,
  canDeleteDocument,
  canEditInGED,
  canFastEditInGED,
  canSendToFriend,
  canSummarize,
  disableTagsRefine,
  handleClose,
  handleToggleAnnotatedTextDisplay,
  isAnnotatedTextDisplayed,
  handleToggleTranslatedTextDisplay,
  isTranslatedTextDisplayed,
  openFastGed,
  patchDocumentInComplete,
  resultsComplete,
  showButtons,
  titleFieldName,
  variant,
  withMoreLikeThis,
}) => {
  const { t } = useTranslation();

  return (
    <Drawer
      anchor="right"
      open
      variant={variant}
      onClose={handleClose}
      sx={variant === 'permanent' ? { flex: '1 1 auto', display: 'flex', overflow: 'hidden' } : {}}
      PaperProps={{
        sx: {
          width: { xs: '85%', md: '70%', lg: '60%' },
          position: variant === 'permanent' ? 'relative' : 'fixed',
        },
      }}
    >
      {!_.isEmpty(resultsComplete.documents) && (
        <List
          dense
          sx={{
            ml: 0,
            pt: 0,
            pb: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'auto',
            flexGrow: '1',
          }}
        >
          {
            resultsComplete.documents.map(
              (document, index) => {
                const title = _.get(
                  document.qesdocument,
                  `${titleFieldName}[0]`,
                  [`[${t('document.title_undefined')}]`],
                );
                return (
                  <ListItem
                    key={document.documentitem}
                    sx={{
                      pt: 0,
                      pr: 0,
                      pl: 2,
                      pb: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'start',
                      alignSelf: 'flex-start',
                      overflow: 'auto',
                      flexGrow: '1',
                    }}
                  >
                    {index > 0 && (
                      <Divider sx={{ mx: 2 }} />
                    )}
                    <Box
                      className="stickyHeader"
                      py={1}
                      zIndex="1"
                      width="100%"
                    >
                      <Typography variant="h5">
                        {title}
                      </Typography>
                      {showButtons && (
                        <Box sx={{ py: 0.5 }}>
                          <ResultSingleActions
                            canAddToCart={canAddToCart}
                            canAddToNewsletter={canAddToNewsletter}
                            canEditInGED={canEditInGED}
                            canFastEditInGed={canFastEditInGED}
                            canDeleteDocument={canDeleteDocument}
                            canSendToFriend={canSendToFriend}
                            canSummarize={canSummarize}
                            document={document}
                            handleOpenFastGed={openFastGed}
                            visible
                          />
                        </Box>
                      )}
                    </Box>
                    <Document
                      isAnnotatedTextDisplayed={isAnnotatedTextDisplayed}
                      isCompleteDisplay
                      disableTagsRefine={disableTagsRefine}
                      document={document}
                      displayTitle={false}
                      patchDocumentInComplete={patchDocumentInComplete}
                      handleToggleAnnotatedTextDisplay={handleToggleAnnotatedTextDisplay}
                      isTranslatedTextDisplayed={isTranslatedTextDisplayed}
                      handleToggleTranslatedTextDisplay={handleToggleTranslatedTextDisplay}
                    />
                    {withMoreLikeThis && (
                      <MoreLikeThis
                        qesdocuments={document.morelikethis}
                        loading={document.loadingMoreLikeThis}
                        moreLikeThisIsVisible
                        showSeeAlsoLabel
                      />
                    )}
                    {canComment && (
                      <CommentsContainer
                        document={document}
                        commentIsVisible
                        autoFocusInput={false}
                      />
                    )}
                  </ListItem>
                );
              },
            )
          }
        </List>
      )}
    </Drawer>
  );
};

ResultsComplete.propTypes = {
  canAddToCart: PropTypes.bool,
  canAddToNewsletter: PropTypes.bool,
  canComment: PropTypes.bool,
  canDeleteDocument: PropTypes.bool,
  canEditInGED: PropTypes.bool,
  canFastEditInGED: PropTypes.bool,
  canSendToFriend: PropTypes.bool,
  canSummarize: PropTypes.bool,
  disableTagsRefine: PropTypes.bool,
  handleClose: PropTypes.func.isRequired,
  handleToggleAnnotatedTextDisplay: PropTypes.func.isRequired,
  isAnnotatedTextDisplayed: PropTypes.bool.isRequired,
  handleToggleTranslatedTextDisplay: PropTypes.func.isRequired,
  isTranslatedTextDisplayed: PropTypes.bool.isRequired,
  openFastGed: PropTypes.func,
  resultsComplete: PropTypes.shape(),
  patchDocumentInComplete: PropTypes.func.isRequired,
  showButtons: PropTypes.bool,
  titleFieldName: PropTypes.string.isRequired,
  variant: PropTypes.oneOf(['temporary', 'permanent']).isRequired,
  withMoreLikeThis: PropTypes.bool,
};

ResultsComplete.defaultProps = {
  canAddToCart: false,
  canAddToNewsletter: false,
  canEditInGED: false,
  canFastEditInGED: false,
  canDeleteDocument: false,
  canSendToFriend: false,
  canSummarize: false,
  canComment: false,
  disableTagsRefine: false,
  withMoreLikeThis: true,
  showButtons: false,
  resultsComplete: {},
  openFastGed: _.noop,
};

export default ResultsComplete;
